import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { AppRole } from "@/types/auth";

export type AppModule = 
  | 'dashboard' 
  | 'agenda' 
  | 'clients' 
  | 'procedures' 
  | 'financial' 
  | 'reports' 
  | 'inventory' 
  | 'admin' 
  | 'settings'
  | 'purchases'
  | 'nfe'
  | 'suppliers'
  | 'marketing'
  | 'medical_records'
  | 'cases'
  | 'club'
  | 'professional_schedule'
  | 'commercial_integration';

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'receive';

export type { AppRole } from "@/types/auth";

export interface RolePermission {
  id: string;
  role: AppRole;
  module: AppModule;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_receive?: boolean;
}


export const MODULE_LABELS: Record<AppModule, string> = {
  dashboard: 'Dashboard',
  agenda: 'Agenda',
  clients: 'Clientes',
  procedures: 'Procedimentos',
  financial: 'Financeiro',
  reports: 'Relatórios',
  inventory: 'Estoque',
  purchases: 'Compras',
  nfe: 'NF-e Recebidas',
  suppliers: 'Fornecedores',
  marketing: 'Marketing',
  medical_records: 'Prontuário',
  cases: 'Casos e Orçamentos',
  club: 'Clube',
  professional_schedule: 'Agenda Profissionais',
  commercial_integration: 'Integração Comercial',
  admin: 'Administração',
  settings: 'Configurações',
};

export const MODULE_DESCRIPTIONS: Record<AppModule, string> = {
  dashboard: 'Painel principal com estatísticas e resumos',
  agenda: 'Gerenciamento de agendamentos e calendário',
  clients: 'Cadastro e gestão de clientes',
  procedures: 'Catálogo de procedimentos estéticos',
  financial: 'Controle financeiro, receitas e despesas',
  reports: 'Relatórios e análises do sistema',
  inventory: 'Controle de estoque e produtos',
  purchases: 'Gestão de ordens de compra e pedidos',
  nfe: 'Importação e gestão de notas fiscais eletrônicas',
  suppliers: 'Cadastro e gestão de fornecedores',
  marketing: 'Gestão de campanhas, leads e performance de marketing',
  medical_records: 'Prontuários, anamneses e fotos clínicas',
  cases: 'Casos de tratamento e orçamentos',
  club: 'Clube e programas de benefícios',
  professional_schedule: 'Horários de atendimento e bloqueios dos profissionais',
  commercial_integration: 'Envio assinado de eventos comerciais para o acompanhamento Tammy',
  admin: 'Painel administrativo e gestão de usuários',
  settings: 'Configurações gerais do sistema',
};

export function usePermissions() {
  const { user, role } = useAuth();

  const { data: permissions = [], isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['role-permissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*')
        .order('module');

      if (error) throw error;
      return data as RolePermission[];
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const hasPermission = (module: AppModule, action: PermissionAction): boolean => {
    // Wait for both role and permissions to be loaded
    if (!role || permissions.length === 0) return false;
    
    const permission = permissions.find(
      p => p.role === role && p.module === module
    );
    
    // If no specific permission found, deny access
    if (!permission) {
      console.log(`[Permissions] No permission found for role=${role}, module=${module}`);
      return false;
    }

    switch (action) {
      case 'view':
        return permission.can_view;
      case 'create':
        return permission.can_create;
      case 'edit':
        return permission.can_edit;
      case 'delete':
        return permission.can_delete;
      case 'receive':
        return !!permission.can_receive || permission.can_create;
      default:
        return false;
    }
  };

  const getPermissionsForRole = (targetRole: AppRole) => {
    return permissions.filter(p => p.role === targetRole);
  };


  return {
    permissions,
    isLoading,
    isFetching,
    isError,
    hasPermission,
    getPermissionsForRole,
    refetch,
  };
}
